import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import useFormattedDate from '@/hooks/useFormattedDate';
import { cn } from '@/lib/utils';
import { TServices } from '@/services/services';
import { TServiceDivision } from '@/services/servicesDivision';
import { getTimeZone } from '@/utils/dates';
import {
  addMinutes,
  eachDayOfInterval,
  format,
  isSameDay,
  startOfDay
} from 'date-fns';
import { enUS, es } from 'date-fns/locale';
import { PenBoxIcon } from 'lucide-react';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

interface GroupDivisionsCardProps {
  division: TServiceDivision;
  service: TServices;
}

const GroupDivisionsCard: React.FC<GroupDivisionsCardProps> = ({
  division,
  service
}) => {
  const { t, i18n } = useTranslation();
  const { formattedDate } = useFormattedDate();
  const { tz } = service.organization;
  const lng = i18n.language === 'es' ? es : enUS;
  // Generar las divisiones del rango para mostrar, filtrando por service.activeDays
  const { items: rangeDivisions, filteredDueToActiveDays } = useMemo(() => {
    if (
      !division?.startTime ||
      !division?.endTime ||
      division?.type !== 'range'
    ) {
      return { items: [], filteredDueToActiveDays: false };
    }

    // Normalizar a inicio de día para cortar por días (usando timezone del servicio)
    const startDateNormalized = startOfDay(getTimeZone(division.startTime, tz));
    const endDateNormalized = startOfDay(getTimeZone(division.endTime, tz));

    // Obtener todos los días en el rango
    const daysInRange = eachDayOfInterval({
      start: startDateNormalized,
      end: endDateNormalized
    });

    // Parse service activeDays (may be stored as JSON string)
    let activeDaysObj: Record<string, Array<[number, number]>> = {};
    try {
      if (service?.activeDays) {
        activeDaysObj =
          typeof service.activeDays === 'string'
            ? JSON.parse(service.activeDays)
            : (service.activeDays as any);
      }
    } catch (e) {
      activeDaysObj = {};
    }

    const hasActiveDaysConfig = Object.keys(activeDaysObj).length > 0;

    // Map each day to a division entry, then filter by activeDays config
    const mapped = daysInRange.map((day, index) => {
      // Crear la fecha/hora específica para esta división usando startHour
      const startDateTime = startOfDay(day);
      // Manejar startHour que puede ser string "HH:MM" o number (minutos)
      let startTimeInMinutes = 0;
      if (division.startHour) {
        if (typeof division.startHour === 'string') {
          const [hours, minutes] = division.startHour.split(':').map(Number);
          startTimeInMinutes = hours * 60 + minutes;
        } else if (typeof division.startHour === 'number') {
          startTimeInMinutes = division.startHour as number;
        }
      }

      const divisionStart = addMinutes(startDateTime, startTimeInMinutes);

      return {
        id: `${division.id}-${index}`,
        name: `${division.name} - Día ${index + 1}`,
        startTime: divisionStart,
        duration: division.duration,
        _day: day
      } as any;
    });

    const filtered = mapped.filter((entry) => {
      if (!hasActiveDaysConfig) return true; // no config => allow all
      const day = entry._day as Date;
      const weekdayKey = format(day, 'eeee', { locale: enUS }).toLowerCase();
      const dayConfig = activeDaysObj[weekdayKey];
      return Array.isArray(dayConfig) && dayConfig.length > 0;
    });

    return {
      items: filtered,
      filteredDueToActiveDays:
        hasActiveDaysConfig && mapped.length > 0 && filtered.length === 0
    };
  }, [division, service?.activeDays, tz]);

  // Validación defensiva para evitar errores si division es undefined
  if (!division) {
    return null;
  }

  const startTime = getTimeZone(division.startTime, tz);
  const endTime = getTimeZone(division.endTime, tz);

  const sameDay = isSameDay(startOfDay(startTime), startOfDay(endTime));

  return (
    <Card key={division.id} className={cn(!division?.active && 'opacity-70')}>
      <CardHeader className="pb-2">
        <div className="flex items-start justify-between">
          <CardTitle className="text-lg font-medium">{division.name}</CardTitle>
          <Button variant="link" size="icon" className="w-8 h-8" asChild>
            <Link to={`./edit-group/${division.id}`}>
              <PenBoxIcon className="w-4 h-4" />
            </Link>
          </Button>
        </div>
        <div className="mt-1 text-sm text-gray-500">
          {sameDay ? (
            <>{formattedDate({ date: startTime, dateFormat: 'date' })}</>
          ) : (
            <>
              {formattedDate({ date: startTime, dateFormat: 'date' })} -{' '}
              {formattedDate({ date: endTime, dateFormat: 'date' })}
            </>
          )}
        </div>
      </CardHeader>
      <CardContent>
        <div className="mb-2 text-sm text-gray-500">
          {t('serviceDetail.divisionConfiguration.divisionsIncluded')}
        </div>
        {rangeDivisions.length === 0 && filteredDueToActiveDays && (
          <div className="mb-2 text-xs rounded-md border border-amber-200 bg-amber-50 px-2 py-1 text-amber-700">
            {t(
              'serviceDetail.divisionConfiguration.noRangeDivisionsDueToActiveDays'
            )}
          </div>
        )}
        <div className="space-y-1 max-h-[150px] overflow-y-auto">
          {rangeDivisions?.map((rangeDivision) => (
            <div
              key={rangeDivision.id}
              className="flex items-center justify-between p-2 text-sm rounded-md bg-gray-50"
            >
              <div>
                <span className="font-medium">{rangeDivision.name}</span>
                <span className="ml-2 text-xs text-gray-500">
                  {formattedDate({
                    date: rangeDivision.startTime,
                    dateFormat: 'dateTime'
                  })}{' '}
                  -
                  {` ${formattedDate({
                    date: addMinutes(
                      rangeDivision.startTime,
                      rangeDivision.duration || 0
                    ),
                    dateFormat: 'time'
                  })}`}
                </span>
              </div>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
};

export default GroupDivisionsCard;
